/**
 * Pending charges. Ported from `TransactionRecord.swift` and `BudgetStore.swift`.
 *
 * A pending charge is a hold: the bank has reserved the money but not settled
 * it. It later posts as its own row, and the scraper joins the two. Between
 * those moments the same purchase can be visible twice, so counting both would
 * double-charge the budget.
 */

import { daysBetweenLedgerDates, ledgerToday } from './ledger';
import { roundCents } from './money';
import { lifecycleEventsOf, Transaction } from './types';

/** A pending older than this with no posted twin is treated as unresolved. */
export const STALE_THRESHOLD_DAYS = 5;

export function ageInDays(transaction: Transaction, now: Date = new Date()): number {
  return Math.max(
    0,
    daysBetweenLedgerDates(transaction.transactionDate.slice(0, 10), ledgerToday(now))
  );
}

export function hasJoinHistory(transaction: Transaction): boolean {
  return lifecycleEventsOf(transaction).some(
    (event) => event.kind === 'pending' || event.kind === 'posted'
  );
}

/** They confirmed this posted charge never had a hold we could link. */
export function hasAcknowledgedNoHold(transaction: Transaction): boolean {
  return lifecycleEventsOf(transaction).some((event) => event.kind === 'direct');
}

/**
 * Posted from the bank and never joined to its hold. Chase always authorized
 * first — sorting this before linking files the same purchase twice. Manual
 * rows never had a hold.
 */
export function needsHoldMatch(transaction: Transaction): boolean {
  if (transaction.status !== 'posted') return false;
  if (transaction.deletedAt != null) return false;
  if (hasJoinHistory(transaction)) return false;
  if (transaction.source === 'manual') return false;
  if (hasAcknowledgedNoHold(transaction)) return false;
  return true;
}

/**
 * The scraper saw that Chase stopped reporting this hold, which means it settled
 * upstream without ever being linked here.
 */
export function isFlaggedUnresolved(transaction: Transaction): boolean {
  return lifecycleEventsOf(transaction).some((event) => event.kind === 'unresolved');
}

/**
 * A hold with no posted twin that is old enough to be suspicious, or one the
 * scraper already proved is gone from Chase. These need a decision, not a
 * category.
 */
export function isUnresolvedPending(transaction: Transaction, now: Date = new Date()): boolean {
  if (transaction.status !== 'pending') return false;
  if (hasJoinHistory(transaction)) return false;
  if (isWaitingForMoreTime(transaction, now)) return false;
  if (isFlaggedUnresolved(transaction)) return true;
  return ageInDays(transaction, now) >= STALE_THRESHOLD_DAYS;
}

/** A recent "wait a few more days" keeps the hold off the resolve list. */
export const WAIT_LONGER_DAYS = 2;

export function isWaitingForMoreTime(transaction: Transaction, now: Date = new Date()): boolean {
  const events = lifecycleEventsOf(transaction);
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const event = events[i];
    if (event.kind !== 'waiting' || !event.at) continue;
    return daysBetweenLedgerDates(event.at.slice(0, 10), ledgerToday(now)) < WAIT_LONGER_DAYS;
  }
  return false;
}

/** The change from authorized to settled — a restaurant tip, usually. */
export function amountAdjustment(transaction: Transaction): number | null {
  const { authAmount, postedAmount } = transaction;
  if (authAmount == null || postedAmount == null) return null;
  const delta = roundCents(postedAmount - authAmount);
  return Math.abs(delta) >= 0.01 ? delta : null;
}

export interface PendingSummary {
  /** Holds still standing against the balance. */
  open: Transaction[];
  /** Negative: money reserved out of the balance. */
  holdsTotal: number;
  /** Positive: money on its way in that has not settled. */
  depositsTotal: number;
  holdCount: number;
  /** Holds that settled upstream and are no longer counted. */
  ghostHoldsTotal: number;
  unresolved: Transaction[];
}

/**
 * Holds are kept apart from incoming money on purpose.
 *
 * Netting the two turned $1,086 of real holds into a "pending holds" figure of
 * +$5,913 the day a $7,000 transfer landed.
 */
export function pendingSummary(
  transactions: readonly Transaction[],
  now: Date = new Date()
): PendingSummary {
  const pending = transactions.filter(
    (transaction) => transaction.deletedAt == null && transaction.status === 'pending'
  );
  const open = pending.filter((transaction) => !isFlaggedUnresolved(transaction));

  return {
    open,
    holdsTotal: roundCents(
      open.filter((t) => t.amount < 0).reduce((total, t) => total + t.amount, 0)
    ),
    depositsTotal: roundCents(
      open.filter((t) => t.amount > 0).reduce((total, t) => total + t.amount, 0)
    ),
    holdCount: open.filter((t) => t.amount < 0).length,
    ghostHoldsTotal: roundCents(
      pending
        .filter((t) => isFlaggedUnresolved(t) && t.amount < 0)
        .reduce((total, t) => total + t.amount, 0)
    ),
    unresolved: pending
      .filter((transaction) => isUnresolvedPending(transaction, now))
      .sort((a, b) => a.transactionDate.localeCompare(b.transactionDate)),
  };
}

export interface LedgerTotals {
  /** Settled money only — the ledger balance. */
  posted: number;
  /** Ledger balance plus everything still in flight — the spendable balance. */
  available: number;
}

export function ledgerTotals(transactions: readonly Transaction[]): LedgerTotals {
  const active = transactions.filter((transaction) => transaction.deletedAt == null);
  const posted = roundCents(
    active
      .filter((transaction) => transaction.status === 'posted')
      .reduce((total, transaction) => total + transaction.amount, 0)
  );
  const { holdsTotal, depositsTotal } = pendingSummary(active);
  return { posted, available: roundCents(posted + holdsTotal + depositsTotal) };
}
